/**
 * NotificationBell — bell icon in TopBar with unread badge.
 *
 * Implements AC35: bell icon with unread count badge (1–99, then '99+'),
 * subtle shake animation on new notification, aria-label with count.
 */

import React, { useState, useEffect, useRef } from 'react';
import { Bell } from 'lucide-react';
import { useNotifications } from './NotificationContext.js';
import { NotificationCentre } from './NotificationCentre.js';

interface NotificationBellProps {
  className?: string;
}

export function NotificationBell({ className = '' }: NotificationBellProps) {
  const { totalUnread } = useNotifications();
  const safeUnread = totalUnread ?? 0;
  const [isOpen, setIsOpen] = useState(false);
  const [shake, setShake] = useState(false);
  const prevUnreadRef = useRef(safeUnread);
  const containerRef = useRef<HTMLDivElement>(null);

  // Shake animation when new notification arrives
  useEffect(() => {
    if (safeUnread > prevUnreadRef.current) {
      setShake(true);
      const timer = setTimeout(() => setShake(false), 600);
      return () => clearTimeout(timer);
    }
    prevUnreadRef.current = safeUnread;
  }, [safeUnread]);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;

    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const badgeLabel = safeUnread === 0
    ? 'Notifications, no unread'
    : safeUnread > 99
      ? 'Notifications, 99+ unread'
      : `Notifications, ${safeUnread} unread`;

  const displayCount = safeUnread === 0
    ? null
    : safeUnread > 99
      ? '99+'
      : String(safeUnread);

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        aria-label={badgeLabel}
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        className={`
          relative p-2 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100
          focus:outline-none focus:ring-1 focus:ring-blue-500 transition-colors
          ${shake ? 'animate-[shake_0.5s_ease-in-out]' : ''}
        `}
      >
        <Bell className="w-5 h-5" />

        {displayCount !== null && (
          <span
            role="status"
            aria-live="polite"
            aria-label={`${safeUnread} unread notifications`}
            className="
              absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1
              bg-red-500 text-white text-xs font-bold rounded-full
              flex items-center justify-center leading-none
            "
          >
            {displayCount}
          </span>
        )}
      </button>

      {isOpen && (
        <NotificationCentre
          open={isOpen}
          onOpenChange={setIsOpen}
        />
      )}

      {/* Shake keyframe via inline style */}
      <style>{`
        @keyframes shake {
          0%, 100% { transform: rotate(0deg); }
          10%, 30%, 50%, 70%, 90% { transform: rotate(-8deg); }
          20%, 40%, 60%, 80% { transform: rotate(8deg); }
        }
      `}</style>
    </div>
  );
}
